import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, FileSpreadsheet, XCircle } from 'lucide-react';
import { FileDropzone } from './FileDropzone';
import { ApiError } from '../../lib/api';
import {
  confirmTeamImport,
  previewTeamImport,
  type TeamImportPreview,
  type TeamImportResult,
  type TeamPersonRow,
} from '../../lib/onboardingImportApi';
import { ProjectMappingPanel, suggestedProjectMap } from '../imports/ProjectMappingPanel';

// Setări → Onboarding → Echipă: 06_echipa_subcontractori.xlsx.
//   1. Upload  — the file (the firm comes from the onboarding page).
//   2. Preview — every person (new / firm data filled / unchanged) and every
//      project period, the project codes to create or map, then confirm.
//   3. Result  — counts + per-row failures.
// Same contract as the invoice import: the confirm re-sends the file and the
// server refuses it if the result no longer matches this preview.

const MAX_BYTES = 10 * 1024 * 1024;

type Step = 'upload' | 'preview' | 'result';

const ACTION_LABEL: Record<TeamPersonRow['action'], string> = {
  create: 'Nouă',
  update: 'Completează firma',
  unchanged: 'Există deja',
  skip: 'Omisă',
};

export function TeamImportDialog({
  companyId,
  onClose,
  onImportComplete,
}: {
  companyId: string;
  onClose: () => void;
  onImportComplete: (result: TeamImportResult) => void;
}) {
  const qc = useQueryClient();
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<TeamImportPreview | null>(null);
  const [projectMap, setProjectMap] = useState<Record<string, string>>({});
  const [result, setResult] = useState<TeamImportResult | null>(null);
  const [busy, setBusy] = useState<'reading' | 'importing' | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function runPreview(f: File, map: Record<string, string>) {
    setBusy('reading');
    setError(null);
    try {
      const first = await previewTeamImport(f, { companyId, projectMap: map });
      const suggested = Object.keys(map).length === 0 ? suggestedProjectMap(first.projects) : {};
      if (Object.keys(suggested).length > 0) {
        setProjectMap(suggested);
        setPreview(await previewTeamImport(f, { companyId, projectMap: suggested }));
      } else {
        setPreview(first);
      }
      setStep('preview');
    } catch (err) {
      setError(messageOf(err, 'Fișierul nu a putut fi citit.'));
    } finally {
      setBusy(null);
    }
  }

  function handleFile(f: File) {
    setError(null);
    if (!/\.xlsx$/i.test(f.name)) {
      setError('Este acceptat doar un fișier .xlsx.');
      return;
    }
    if (f.size > MAX_BYTES) {
      setError('Fișierul depășește limita de 10MB.');
      return;
    }
    setFile(f);
    setProjectMap({});
    void runPreview(f, {});
  }

  async function handleImport() {
    if (!file || !preview) return;
    setBusy('importing');
    setError(null);
    try {
      const res = await confirmTeamImport(file, {
        companyId,
        previewHash: preview.previewHash,
        projectMap,
      });
      setResult(res);
      setStep('result');
      void qc.invalidateQueries({ queryKey: ['people'] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
      onImportComplete(res);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'PREVIEW_CHANGED') {
        setError(err.message);
        void runPreview(file, projectMap);
      } else {
        setError(messageOf(err, 'Importul a eșuat.'));
      }
    } finally {
      setBusy(null);
    }
  }

  const s = preview?.summary;
  const applicable = (s?.create ?? 0) + (s?.update ?? 0);
  const periodsChanged =
    preview?.periods.filter((p) => p.action === 'create' || p.action === 'update').length ?? 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-brand-600" />
            <h2 className="text-lg font-semibold text-gray-900">Importă echipa din Excel</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy === 'importing'}
            className="text-gray-400 hover:text-gray-600"
            aria-label="Închide"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {error && (
            <div className="mb-4 flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              <XCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {step === 'upload' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Fișierul <span className="font-mono">06_echipa_subcontractori.xlsx</span>: foaia
                ECHIPA B2B (o persoană pe rând, cu firma, CUI-ul și IBAN-ul ei) și foaia PROIECTE PE
                PERIOADE (proiectul fiecărei persoane, pe perioade). Colaboratorii devin persoane în
                Echipă, nu furnizori.
              </p>
              <FileDropzone
                accept=".xlsx"
                hint="Un fișier .xlsx, până la 10MB"
                disabled={busy !== null}
                onFile={handleFile}
              />
              {busy === 'reading' && (
                <Spinner text="Se citește fișierul și se compară cu Firmio…" />
              )}
            </div>
          )}

          {step === 'preview' && preview && s && (
            <>
              <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-5">
                <Stat label="Persoane noi" value={s.create} tone="green" />
                <Stat label="Firmă completată" value={s.update} tone="blue" />
                <Stat label="Există deja" value={s.unchanged} tone="gray" />
                <Stat label="Perioade noi" value={s.periodsNew} tone="green" />
                <Stat label="Erori" value={s.errors} tone="red" />
              </div>

              <ProjectMappingPanel
                projects={preview.projects}
                projectMap={projectMap}
                onChange={(next) => {
                  setProjectMap(next);
                  if (file) void runPreview(file, next);
                }}
                disabled={busy !== null}
              />

              <h3 className="mb-1 text-sm font-semibold text-gray-800">Persoane</h3>
              <div className="mb-4 max-h-[260px] overflow-auto rounded-md border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200 text-xs">
                  <thead className="sticky top-0 bg-gray-50 text-left font-semibold uppercase tracking-wide text-gray-600">
                    <tr>
                      <th className="px-2 py-2">Rând</th>
                      <th className="px-2 py-2" />
                      <th className="px-2 py-2">Persoană</th>
                      <th className="px-2 py-2">Firmă · CUI</th>
                      <th className="px-2 py-2">IBAN</th>
                      <th className="px-2 py-2">Ce se întâmplă</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {preview.people.map((r) => (
                      <tr key={r.rowNumber} className={r.status === 'error' ? 'bg-red-500/10' : ''}>
                        <td className="px-2 py-1.5 align-top text-gray-500">{r.rowNumber}</td>
                        <td className="px-2 py-1.5 align-top">
                          <StatusIcon status={r.status} />
                        </td>
                        <td className="px-2 py-1.5 align-top">
                          <span className="font-medium text-gray-900">{r.data.fullName}</span>
                          {r.data.email && (
                            <span className="block text-gray-500">{r.data.email}</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 align-top text-gray-700">
                          {r.data.companyName ?? '—'}
                          {r.data.companyCui && (
                            <span className="block text-gray-500">{r.data.companyCui}</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 align-top font-mono text-gray-600">
                          {r.data.iban ?? '—'}
                        </td>
                        <td className="px-2 py-1.5 align-top">
                          <span className="text-gray-800">
                            {r.status === 'error' ? 'Eroare' : ACTION_LABEL[r.action]}
                          </span>
                          <Issues status={r.status} issues={r.issues} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <h3 className="mb-1 text-sm font-semibold text-gray-800">Proiecte pe perioade</h3>
              <div className="max-h-[260px] overflow-auto rounded-md border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200 text-xs">
                  <thead className="sticky top-0 bg-gray-50 text-left font-semibold uppercase tracking-wide text-gray-600">
                    <tr>
                      <th className="px-2 py-2">Rând</th>
                      <th className="px-2 py-2" />
                      <th className="px-2 py-2">Persoană</th>
                      <th className="px-2 py-2">Proiect</th>
                      <th className="px-2 py-2">Perioadă</th>
                      <th className="px-2 py-2 text-right">Cotă</th>
                      <th className="px-2 py-2">Ce se întâmplă</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {preview.periods.map((r) => (
                      <tr key={r.rowNumber} className={r.status === 'error' ? 'bg-red-500/10' : ''}>
                        <td className="px-2 py-1.5 align-top text-gray-500">{r.rowNumber}</td>
                        <td className="px-2 py-1.5 align-top">
                          <StatusIcon status={r.status} />
                        </td>
                        <td className="px-2 py-1.5 align-top text-gray-900">{r.data.label}</td>
                        <td className="px-2 py-1.5 align-top font-mono text-gray-600">
                          {r.data.projectCode || '—'}
                        </td>
                        <td className="px-2 py-1.5 align-top text-gray-700">
                          {r.data.from ?? 'de la început'} → {r.data.to ?? 'prezent'}
                        </td>
                        <td className="px-2 py-1.5 text-right align-top text-gray-700">
                          {r.data.share === null ? '—' : `${Math.round(r.data.share * 100)}%`}
                        </td>
                        <td className="px-2 py-1.5 align-top">
                          <span className="text-gray-800">
                            {r.status === 'error'
                              ? 'Eroare'
                              : r.action === 'create'
                                ? 'Nouă'
                                : r.action === 'update'
                                  ? 'Actualizare'
                                  : r.action === 'unchanged'
                                    ? 'Există deja'
                                    : 'Omisă'}
                          </span>
                          <Issues status={r.status} issues={r.issues} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {step === 'result' && result && (
            <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 p-4">
              <p className="flex items-center gap-2 text-sm font-semibold text-emerald-700">
                <CheckCircle2 className="h-4 w-4" />
                Import finalizat
              </p>
              <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-5">
                <Stat label="Persoane create" value={result.peopleCreated} tone="green" />
                <Stat label="Persoane completate" value={result.peopleUpdated} tone="blue" />
                <Stat
                  label="Perioade"
                  value={result.periodsCreated + result.periodsUpdated}
                  tone="green"
                />
                <Stat label="Proiecte create" value={result.projectsCreated} tone="green" />
                <Stat label="Eșuate" value={result.failed.length} tone="red" />
              </div>
              {result.failed.length > 0 && (
                <ul className="mt-3 space-y-1 text-sm text-red-700">
                  {result.failed.map((f) => (
                    <li key={f.rowNumber}>
                      Rândul {f.rowNumber}: {f.error}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
          {step === 'preview' && (
            <>
              {busy === 'reading' && <Spinner text="Se recalculează…" />}
              <button
                type="button"
                onClick={() => {
                  setStep('upload');
                  setPreview(null);
                  setFile(null);
                }}
                disabled={busy !== null}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Alege alt fișier
              </button>
              <button
                type="button"
                onClick={() => void handleImport()}
                disabled={busy !== null || applicable + periodsChanged === 0}
                className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {busy === 'importing'
                  ? 'Se importă…'
                  : `Importă (${s?.create ?? 0} persoane noi, ${periodsChanged} perioade)`}
              </button>
            </>
          )}
          {step !== 'preview' && (
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {step === 'result' ? 'Gata' : 'Anulează'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Issues({ status, issues }: { status: TeamPersonRow['status']; issues: string[] }) {
  return (
    <>
      {issues.map((issue, i) => (
        <span key={i} className={`block ${status === 'error' ? 'text-red-700' : 'text-amber-700'}`}>
          {issue}
        </span>
      ))}
    </>
  );
}

function Spinner({ text }: { text: string }) {
  return (
    <p className="flex items-center gap-2 text-sm text-gray-500">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-brand-500 border-t-transparent" />
      {text}
    </p>
  );
}

function StatusIcon({ status }: { status: TeamPersonRow['status'] }) {
  if (status === 'valid')
    return <CheckCircle2 className="h-4 w-4 text-green-600" aria-label="Valid" />;
  if (status === 'warning')
    return <AlertTriangle className="h-4 w-4 text-amber-500" aria-label="Avertisment" />;
  return <XCircle className="h-4 w-4 text-red-600" aria-label="Eroare" />;
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'green' | 'blue' | 'red' | 'gray';
}) {
  const toneClass = {
    green: 'text-green-700',
    blue: 'text-blue-700',
    red: 'text-red-700',
    gray: 'text-gray-700',
  }[tone];
  return (
    <div className="rounded border border-gray-200 bg-white px-3 py-2 text-center">
      <p className="text-xs text-gray-500">{label}</p>
      <p className={`text-lg font-semibold ${toneClass}`}>{value}</p>
    </div>
  );
}

function messageOf(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}
